import { useMemo } from "react";
import { ChevronRight, Mic, Plus, Sparkles } from "lucide-react";
import { Link } from "react-router";
import {
  WEATHER_LABELS,
  type ExtractedDelivery,
  type ExtractedIncident,
  type JournalDaySummary,
  type JournalNote,
  EXTRACTED_DELIVERY_CONDITION_LABELS_STANDALONE,
  EXTRACTED_INCIDENT_SEVERITY_LABELS,
} from "@gb/contracts";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { addDays, DISPLAY_LOCALE } from "@/lib/format";
import { cn } from "@/lib/utils";
import { SEVERITY_STYLES } from "./incidents";

/**
 * The entry feed — days, newest first, and what was filed against each.
 *
 * **Rows are not one-to-one with notes.** One note becomes one *Daily log* row
 * carrying its prose, plus one *Delivery* row per delivery a model read out of
 * that prose. `Daily log` and `Delivery` are presentation-layer tags computed
 * from which array a row came out of; no type enum exists in the schema,
 * because there is nothing to store (PRD §7.7).
 *
 * The derived rows are marked, deliberately and repeatedly — a sunken
 * background, an inset rule, a spark and the words "Read from this note". They
 * are a model's reading sitting next to the sentence it was read from, and
 * `document-ai/0002` is explicit that a badge is not a check. The prose is
 * right there to check them against, which is the whole reason they render
 * beside it rather than being folded into the day's record.
 */

// ------------------------------------------------------------------
// Dates
// ------------------------------------------------------------------
//
// ISO days are parsed as UTC throughout, exactly as `lib/format.ts` does, so a
// date never slides a day either way depending on where the reader is sitting.
//
// `todayIso` deliberately is **not** redefined here. It lives beside the
// calendar grid that paints the Today fill, and a second definition that
// disagreed by a timezone would put the prompt on one date and the fill on
// another.

/**
 * `Mon, 1 Jun 2026` — the weekday matters on a site record.
 *
 * Unchanged from the list this feed replaces, down to the comma en-GB puts
 * after a weekday, so a date reads identically here and on the day screen.
 *
 * Lives here rather than on the screen because the feed is where days are
 * rendered; `journal-screen.tsx` re-exports it, which is where
 * `journal-entry-screen.tsx` has always imported it from.
 */
export function formatJournalDate(value: string): string {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(DISPLAY_LOCALE, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** `Friday 5 June` — the new-entry prompt names the day, not the year. */
export function formatDayName(value: string): string {
  return new Date(`${value}T00:00:00Z`).toLocaleDateString(DISPLAY_LOCALE, {
    weekday: "long",
    day: "numeric",
    month: "long",
    timeZone: "UTC",
  });
}

/**
 * `07:40` — when the note was filed, in the reader's own clock.
 *
 * A timestamp, unlike a date, genuinely is an instant, so this one is *not*
 * pinned to UTC: a delivery logged at 07:40 on site should read 07:40.
 */
export function formatNoteTime(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return "";
  return at.toLocaleTimeString(DISPLAY_LOCALE, {
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ------------------------------------------------------------------
// Decomposition
// ------------------------------------------------------------------

export type FeedRow =
  | { kind: "log"; key: string; note: JournalNote }
  | {
      kind: "delivery";
      key: string;
      note: JournalNote;
      delivery: ExtractedDelivery;
    }
  | {
      kind: "incident";
      key: string;
      note: JournalNote;
      incident: ExtractedIncident;
    };

/**
 * One note, as the feed shows it: its prose, then each delivery and each
 * incident read out of it.
 *
 * The prose row comes first and keeps the timestamp; derived rows carry the
 * tag and the author but no time of their own, because the note was filed
 * once and a delivery inside it did not happen at the moment it was typed.
 */
export function decomposeNote(note: JournalNote): FeedRow[] {
  return [
    { kind: "log", key: `${note.id}-log`, note },
    ...note.deliveries.map((delivery, i) => ({
      kind: "delivery" as const,
      key: `${note.id}-delivery-${i}`,
      note,
      delivery,
    })),
    ...note.incidents.map((incident, i) => ({
      kind: "incident" as const,
      key: `${note.id}-incident-${i}`,
      note,
      incident,
    })),
  ];
}

export type FeedDay = { date: string; noteCount: number; rows: FeedRow[] };

/**
 * Notes grouped into days, most recent day first.
 *
 * The API already answers `date desc, created_at desc`, so this preserves the
 * order it was given within a day and only sorts the days themselves — which
 * keeps the grouping correct even if a filtered response ever arrives ordered
 * some other way.
 */
export function groupByDay(notes: readonly JournalNote[]): FeedDay[] {
  const byDate = new Map<string, JournalNote[]>();
  for (const note of notes) {
    const bucket = byDate.get(note.date);
    if (bucket) bucket.push(note);
    else byDate.set(note.date, [note]);
  }
  return [...byDate.entries()]
    .sort(([a], [b]) => (a < b ? 1 : a > b ? -1 : 0))
    .map(([date, dayNotes]) => ({
      date,
      noteCount: dayNotes.length,
      rows: dayNotes.flatMap(decomposeNote),
    }));
}

// ------------------------------------------------------------------
// Labels
// ------------------------------------------------------------------

/**
 * The words come from the contract; the Tailwind does not.
 *
 * The extraction enums carry an `unknown` member the contract's own
 * `DeliveryCondition` / `IncidentSeverity` do not — it is not a fifth
 * condition, it is the absence of one, and it reads as "not recorded" rather
 * than as a zero or a guess (`data-model/0004` §2.4).
 *
 * These rows show a condition as a lone chip with no `Condition` heading beside
 * it, which is why they take the `_STANDALONE` rendering — *"OK"* on its own
 * does not say what is OK. The composer, which does have a heading, takes the
 * bare one.
 */
const CONDITION_LABELS = EXTRACTED_DELIVERY_CONDITION_LABELS_STANDALONE;

const CONDITION_TONES: Record<ExtractedDelivery["condition"], string> = {
  ok: "text-ok",
  damaged: "text-danger",
  "wrong-spec": "text-danger",
  unknown: "text-muted",
};

const SEVERITY_LABELS = EXTRACTED_INCIDENT_SEVERITY_LABELS;

const EXTRACTED_SEVERITY_STYLES: Record<ExtractedIncident["severity"], string> =
  {
    ...SEVERITY_STYLES,
    unknown: "border-border bg-canvas text-muted",
  };

// ------------------------------------------------------------------
// The feed
// ------------------------------------------------------------------

export type JournalFeedProps = {
  projectId: string;
  notes: readonly JournalNote[];
  /** Per-date facts, for the day headers. */
  daysByDate: ReadonlyMap<string, JournalDaySummary>;
  /** Today, as the site sees it. */
  today: string;
  /**
   * The date the pinned prompt files against, or `null` for none.
   *
   * Usually today, but a single selected past day is its own subject — see
   * `journal-screen.tsx`. Whether that date already has its record chooses the
   * prompt's *wording* and not whether it renders: a day may hold several notes
   * and still be missing its record (PRD §7.6), and equally may have its record
   * and still be owed a note. See `NewEntryPrompt`.
   */
  promptDate: string | null;
  /** Date → that day's `journal_entries` id, when there is one to open. */
  entryIdByDate: ReadonlyMap<string, string>;
  /** Whether a search is narrowing the feed. Changes the empty line only. */
  searching: boolean;
  onCompose: (date: string) => void;
};

export function JournalFeed({
  projectId,
  notes,
  daysByDate,
  today,
  promptDate,
  entryIdByDate,
  searching,
  onCompose,
}: JournalFeedProps) {
  const days = useMemo(() => groupByDay(notes), [notes]);

  /**
   * Days with notes, interleaved with the gaps between them.
   *
   * A gap is a date inside the span the feed already covers that holds no
   * notes at all — derived, never a row: nothing is written for a day nobody
   * worked (`data-model/0004` §1). The left rail lists the last fortnight's
   * gaps; this puts them where the eye already is, in date order, so a missing
   * day reads as a hole in the record rather than as nothing at all.
   *
   * Suppressed while searching. A filtered feed is *expected* to skip days,
   * and captioning every one of them "nothing logged" would be false.
   */
  const rows = useMemo(() => {
    if (searching || days.length === 0)
      return days.map((day) => ({ day, gap: null }));

    const first = days[0];
    const last = days[days.length - 1];
    if (!first || !last) return days.map((day) => ({ day, gap: null }));

    const out: { day: FeedDay | null; gap: string | null }[] = [];
    const newest = first.date;
    const oldest = last.date;
    const byDate = new Map(days.map((day) => [day.date, day]));

    for (let cursor = newest; cursor >= oldest; cursor = addDays(cursor, -1)) {
      const day = byDate.get(cursor);
      if (day) out.push({ day, gap: null });
      // The prompt's date has its own pinned row at the top of the feed; a
      // second affordance for it, five pixels lower, is not a reminder.
      else if (cursor !== promptDate) out.push({ day: null, gap: cursor });
    }
    return out;
  }, [days, searching, promptDate]);

  return (
    <div className="flex flex-col gap-4">
      {/* First in the feed, and deliberately *not* `position: sticky`. Nothing
          in this app positions anything sticky inside the scroll column, and
          `architecture/0001` is not a record to go testing on this feature. */}
      {promptDate && (
        <NewEntryPrompt
          date={promptDate}
          isToday={promptDate === today}
          hasRecord={daysByDate.get(promptDate)?.hasDailyEntry ?? false}
          onOpen={() => onCompose(promptDate)}
        />
      )}

      {rows.map(({ day, gap }) =>
        day ? (
          <DayGroup
            key={day.date}
            day={day}
            summary={daysByDate.get(day.date)}
            entryId={entryIdByDate.get(day.date)}
            projectId={projectId}
            // The prompt's own date is the one day that already has an
            // affordance, pinned directly above this card. Two ways in for one
            // date, a few pixels apart, is the thing gap rows already refuse.
            onAddNote={
              day.date === promptDate ? null : () => onCompose(day.date)
            }
          />
        ) : (
          <GapRow key={gap!} date={gap!} onFillIn={() => onCompose(gap!)} />
        ),
      )}

      {days.length === 0 && (
        <p className="px-1 py-8 text-center text-[0.88rem] text-muted">
          {searching
            ? "No notes match that."
            : "Nothing filed against these dates yet."}
        </p>
      )}
    </div>
  );
}

/**
 * The way into the composer for **the day the feed is about** — rendered
 * whatever that day already holds, in one of two states.
 *
 * Two corrections to PRD §7.6 live here, and both were the same mistake:
 * treating "today, and only while unlogged" as the subject.
 *
 * **It used to render only when today had no daily record**, straight from
 * `jlTodayOpen = !dailyByDate[TODAY]`. That is right about a *prompt* and wrong
 * about a *button*: it is the feed's only entry point for its date — no gap row
 * is emitted for it, the empty state is gone once anything is filed — so hiding
 * it left no way to file a second note against a day, which is the entire
 * premise of a note and a day being different grains. A day takes a delivery at
 * 07:40 and a near-miss at 16:20 whether or not it has been written up.
 *
 * **And its date used to be today unconditionally**, which was invisible until
 * the above made it render always: selecting 4 August then showed a prompt
 * naming 5 August, above an empty 4 August, and filed against the 5th.
 * `journal-screen.tsx` owns which date that is now.
 *
 * So the render condition moved onto the *copy*: dashed and amber while the
 * record is missing, quiet and ordinary once it is filed. What changes is what
 * the day still needs, not whether the way in exists.
 *
 * The microphone and the supporting line are the design's. The line shipped
 * as *type to start* while dictation was inert (PRD §8), and went back to the
 * design's words when the composer started recording (`product/0014`).
 */
function NewEntryPrompt({
  date,
  isToday,
  hasRecord,
  onOpen,
}: {
  date: string;
  isToday: boolean;
  hasRecord: boolean;
  onOpen: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        "flex w-full cursor-pointer items-center gap-3.5 rounded-card-lg border-[1.5px]",
        "px-5 py-4 text-left transition-colors",
        hasRecord
          ? "border-border bg-surface hover:bg-subtle"
          : "border-dashed border-brand bg-brand-tint-3 hover:bg-brand-tint-2",
      )}
    >
      <span className={cn("flex", hasRecord ? "text-muted" : "text-brand")}>
        <Mic size={20} />
      </span>
      <span className="min-w-0">
        <span className="block font-display text-[0.98rem] font-semibold tracking-[0.02em] text-ink">
          {hasRecord ? "Add a note" : "New entry"} — {formatDayName(date)}
        </span>
        <span
          className={cn(
            "block text-[0.85rem]",
            hasRecord ? "text-muted" : "text-brand-on-tint",
          )}
        >
          {hasRecord
            ? // Not "nothing left to do": the record is filed, and a delivery
              // at 16:20 still belongs on this day. `Today's` only when it is
              // today — the prompt also serves a selected past day.
              `${isToday ? "Today’s" : "This day’s"} record is filed · add a delivery, a hold-up or a near-miss`
            : "Not logged yet · dictate or type to start"}
        </span>
      </span>
    </button>
  );
}

/**
 * One day in the feed.
 *
 * `onAddNote` is `null` only for today, which carries the pinned prompt instead
 * (see the call site). For every other day this is the **only** way to file a
 * second note against it: the calendar and the catch-up queue offer gaps, and a
 * day that already holds a note is by definition not one. Until this existed,
 * backfilling a second note meant opening the composer for today and changing
 * its date — reachable, but not an affordance.
 */
function DayGroup({
  day,
  summary,
  entryId,
  projectId,
  onAddNote,
}: {
  day: FeedDay;
  summary: JournalDaySummary | undefined;
  entryId: string | undefined;
  projectId: string;
  onAddNote: (() => void) | null;
}) {
  // The day summary counts every row the day holds; a searched feed shows only
  // the matching notes, so fall back to what is actually on screen rather than
  // claiming rows that are not rendered.
  const entryCount = summary?.entryCount ?? day.rows.length;

  return (
    <section className="overflow-hidden rounded-card-lg border-[1.5px] border-border bg-surface">
      <header className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b-[1.5px] border-border-inner px-4 py-3">
        <h3 className="font-display text-[0.95rem] font-semibold tracking-[0.03em] text-ink uppercase">
          {formatJournalDate(day.date)}
        </h3>
        <span className="text-[0.8rem] text-muted">
          {entryCount} {entryCount === 1 ? "entry" : "entries"}
        </span>
        {/* Sign-off is the day's, not a note's — and it is a pill, replacing
            the bare "Yes"/"Pending" text the old list rendered. There is no
            `Edited` marker here or anywhere: only JRNL-08 defines generation
            and versioning, so nothing exists to drive one from (§7.7). */}
        {summary?.signedOff && (
          <Badge tone="ok" shape="pill">
            Signed off
          </Badge>
        )}

        <span className="flex-1" />

        {summary && (
          <span
            className="text-[0.8rem] text-muted"
            // A stint is a membership period, not a day's attendance
            // (`data-model/0003`), so this number is a suggestion and says so
            // rather than being presented as a headcount.
            title="Crew with an open stint on this date — a membership count, not attendance."
          >
            {summary.crewCount} crew · {summary.photoCount}{" "}
            {summary.photoCount === 1 ? "photo" : "photos"}
          </span>
        )}

        {onAddNote && (
          // Named with its date. Several of these are on screen at once and
          // "Add note" alone would give them all the same accessible name.
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="shrink-0"
            aria-label={`Add a note to ${formatJournalDate(day.date)}`}
            onClick={onAddNote}
          >
            <Plus aria-hidden size={15} />
            Add note
          </Button>
        )}

        {entryId && (
          <Link
            to={`/projects/${projectId}/journal/${entryId}`}
            aria-label={`Open ${formatJournalDate(day.date)} in full`}
            className="flex text-muted transition-colors hover:text-ink"
          >
            <ChevronRight size={18} />
          </Link>
        )}
      </header>

      <ul>
        {day.rows.map((row) =>
          row.kind === "log" ? (
            <DailyLogRow key={row.key} note={row.note} />
          ) : row.kind === "delivery" ? (
            <DeliveryRow key={row.key} delivery={row.delivery} />
          ) : (
            <IncidentRow key={row.key} incident={row.incident} />
          ),
        )}
      </ul>
    </section>
  );
}

/** The user's own words, with the structured fields they typed alongside. */
function DailyLogRow({ note }: { note: JournalNote }) {
  return (
    <li className="flex gap-3.5 border-t-[1.5px] border-border-faint px-4 py-3.5 first:border-t-0">
      <time
        dateTime={note.createdAt}
        className="w-[46px] shrink-0 pt-[3px] font-mono text-[0.76rem] text-muted"
      >
        {formatNoteTime(note.createdAt)}
      </time>
      <div className="min-w-0 flex-1">
        <div className="mb-1.5 flex flex-wrap items-center gap-2">
          <Badge tone="neutral" shape="pill">
            Daily log
          </Badge>
          {note.isDailyEntry && (
            <Badge tone="brand" shape="pill">
              Day&rsquo;s record
            </Badge>
          )}
          {note.authorName && (
            // Null when the author never set a name. Nothing is substituted —
            // a fallback would put an invented label under somebody's words.
            <span className="text-[0.8rem] text-muted">{note.authorName}</span>
          )}
        </div>

        <p className="text-[0.92rem] leading-[1.55] whitespace-pre-wrap text-ink-prose">
          {note.body}
        </p>

        {/* "Not recorded", never zero. `Number("")` is `0`, and this codebase
            has already shipped a form that quietly filed "nobody on site". */}
        <p className="mt-1.5 text-[0.8rem] text-muted">
          {note.headcount === null
            ? "Headcount not recorded"
            : `${note.headcount} on site`}
          {" · "}
          {note.weather === null
            ? "weather not recorded"
            : WEATHER_LABELS[note.weather]}
        </p>

        {/* Incidents used to be summarised inside this row. They now get rows
            of their own, beside deliveries — the design shows a third tag in
            the feed, and a near-miss folded into a paragraph is exactly the
            thing `product/0002` says the product could not answer. */}

        <ExtractionNote status={note.extractionStatus} />
      </div>
    </li>
  );
}

/**
 * A delivery a model read out of the note above it.
 *
 * Every treatment on this row exists to keep it from reading as something a
 * person typed: it is inset, sunken, marked and captioned. Extraction stops at
 * the note and never reaches `journal_entries` (§5.2), so this row is a
 * reading — not a record.
 */
function DeliveryRow({ delivery }: { delivery: ExtractedDelivery }) {
  return (
    <li className="border-t-[1.5px] border-border-faint py-2.5 pr-4 pl-[62px]">
      <div className="rounded-input border-l-[3px] border-brand-border-strong bg-surface-sunken px-3 py-2.5">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Badge tone="violet" shape="pill">
            Delivery
          </Badge>
          <DerivedMark />
        </div>
        <p className="flex flex-wrap items-baseline gap-x-2 text-[0.9rem]">
          <span className="font-semibold text-ink">
            {delivery.supplier ?? "Supplier not recorded"}
          </span>
          <span className="text-ink-prose">
            {delivery.item ?? "Item not recorded"}
          </span>
          <span className="text-muted">
            {delivery.qty === null
              ? "quantity not recorded"
              : `× ${delivery.qty}`}
          </span>
          <span
            className={cn(
              "text-[0.82rem]",
              CONDITION_TONES[delivery.condition],
            )}
          >
            {CONDITION_LABELS[delivery.condition]}
          </span>
        </p>
      </div>
    </li>
  );
}

/**
 * A day inside the feed's span that nobody logged.
 *
 * Dashed rather than solid, and captioned with what the system *does* hold for
 * that date, because the offer only makes sense if there is something to draft
 * from — the queue's own promise is that the user corrects a draft rather than
 * facing a blank page.
 */
function GapRow({ date, onFillIn }: { date: string; onFillIn: () => void }) {
  return (
    <section className="flex items-center gap-3 rounded-card-lg border-[1.5px] border-dashed border-brand bg-brand-tint-3 px-4 py-3.5">
      <div className="min-w-0 flex-1">
        <h3 className="font-display text-[0.9rem] font-bold tracking-[0.03em] text-brand-on-tint uppercase">
          {formatJournalDate(date)} — nothing logged
        </h3>
        <p className="mt-0.5 text-[0.84rem] leading-[1.45] text-ink-prose">
          Clock-ins, deliveries and photos are on file. Draft a day from them.
        </p>
      </div>
      <Button
        type="button"
        size="sm"
        className="shrink-0 hover:bg-brand-deep"
        onClick={onFillIn}
      >
        Fill in
      </Button>
    </section>
  );
}

/**
 * An incident or near-miss a model read out of the note above it.
 *
 * Tagged `Incident`, not `Near-miss`. The design's one example is a cut to the
 * forearm labelled that way, but "near-miss" is a *severity* — something that
 * nearly happened — and this row renders whatever the note described, up to
 * and including a reportable injury. A reportable injury filed under a pill
 * reading "Near-miss" is a wrong entry in a safety record, so the tag names
 * the row type and the severity is shown beside it, in its own words.
 */
function IncidentRow({ incident }: { incident: ExtractedIncident }) {
  return (
    <li className="border-t-[1.5px] border-border-faint py-2.5 pr-4 pl-[62px]">
      <div className="rounded-input border-l-[3px] border-warn/50 bg-surface-sunken px-3 py-2.5">
        <div className="mb-1 flex flex-wrap items-center gap-2">
          <Badge tone="warn" shape="pill">
            Incident
          </Badge>
          <span
            className={cn(
              "shrink-0 rounded-full border px-2 py-[2px] font-display text-[0.62rem] font-semibold tracking-[0.06em] uppercase",
              EXTRACTED_SEVERITY_STYLES[incident.severity],
            )}
          >
            {SEVERITY_LABELS[incident.severity]}
          </span>
          <DerivedMark />
        </div>
        <p className="text-[0.9rem] leading-[1.5] text-ink-prose">
          {incident.text ?? "Nothing recorded"}
        </p>
      </div>
    </li>
  );
}

/** The four words that separate a reading from a record. */
function DerivedMark() {
  return (
    <span className="inline-flex items-center gap-1.5 text-[0.72rem] font-semibold tracking-[0.04em] text-muted uppercase">
      <Sparkles size={12} />
      Read from this note
    </span>
  );
}

/**
 * How far the model has got.
 *
 * `skipped` renders nothing at all: it means no model is bound, which is a
 * supported configuration and not a failure — the prose is the note either way
 * (`document-ai/0004` §2.3, PRD §5.4).
 */
function ExtractionNote({
  status,
}: {
  status: JournalNote["extractionStatus"];
}) {
  if (status === "pending") {
    return (
      <p className="mt-2 text-[0.78rem] text-muted italic">
        Still reading this note for deliveries.
      </p>
    );
  }
  if (status === "failed") {
    return (
      <p className="mt-2 text-[0.78rem] text-muted italic">
        Couldn&rsquo;t read deliveries out of this note. The note itself is
        filed.
      </p>
    );
  }
  return null;
}
